/**
 * mSTEM user projects reducer (PRJ-01..03 / LLD UC 06-08).
 *
 * Holds the in-memory list of the user's saved projects + the id of the
 * project currently loaded into the VM. Seeded at module load from
 * src/lib/mstem-mock/projects.js; replaced at runtime via SEED_PROJECTS
 * when hydrating from localStorage on app boot.
 *
 * Security note (T-03-01-03): CREATE_ENTRY + UPDATE_ENTRY validate the
 * incoming entry via `isValidEntry` — empty name, name > 100 chars,
 * missing id, description > 2000 chars all return the same state object
 * reference. CREATE_ENTRY additionally rejects duplicate ids OR duplicate
 * names (LLD UC 06 requires unique project names).
 */

import MOCK_PROJECTS from '../lib/mstem-mock/projects';

const CREATE_ENTRY = 'scratch-gui/my-projects/CREATE_ENTRY';
const UPDATE_ENTRY = 'scratch-gui/my-projects/UPDATE_ENTRY';
const DELETE_ENTRY = 'scratch-gui/my-projects/DELETE_ENTRY';
const SEED_PROJECTS = 'scratch-gui/my-projects/SEED_PROJECTS';
const SET_CURRENT_PROJECT = 'scratch-gui/my-projects/SET_CURRENT_PROJECT';

const MAX_NAME_LENGTH = 100;
const MAX_DESC_LENGTH = 2000;

const initialState = {
    list: MOCK_PROJECTS.slice(),
    currentId: null
};

const isValidEntry = function (p) {
    if (!p || typeof p.name !== 'string') return false;
    const name = p.name.trim();
    if (name.length === 0 || name.length > MAX_NAME_LENGTH) return false;
    if (typeof p.id !== 'string' || p.id.length === 0) return false;
    if (typeof p.description === 'string' &&
        p.description.length > MAX_DESC_LENGTH) return false;
    return true;
};

const reducer = function (state, action) {
    if (typeof state === 'undefined') state = initialState;
    switch (action.type) {
    case SEED_PROJECTS:
        if (!Array.isArray(action.list)) return state;
        return Object.assign({}, state, {list: action.list.slice()});
    case CREATE_ENTRY:
        if (!isValidEntry(action.entry)) return state;
        // Reject duplicate ids — preserves uniqueness invariant for delete/edit.
        if (state.list.some(p => p.id === action.entry.id)) return state;
        // Reject duplicate names — LLD UC 06 explicit constraint.
        if (state.list.some(p => p.name === action.entry.name)) return state;
        return Object.assign({}, state, {
            list: state.list.concat([action.entry])
        });
    case UPDATE_ENTRY:
        if (!isValidEntry(action.entry)) return state;
        return Object.assign({}, state, {
            list: state.list.map(p => (
                p.id === action.entry.id ? Object.assign({}, p, action.entry) : p
            ))
        });
    case DELETE_ENTRY:
        if (typeof action.id !== 'string') return state;
        return Object.assign({}, state, {
            list: state.list.filter(p => p.id !== action.id)
        });
    case SET_CURRENT_PROJECT:
        return Object.assign({}, state, {currentId: action.id});
    default:
        return state;
    }
};

const createProjectEntry = function (entry) {
    return {type: CREATE_ENTRY, entry: entry};
};
const updateProjectEntry = function (entry) {
    return {type: UPDATE_ENTRY, entry: entry};
};
const deleteProjectEntry = function (id) {
    return {type: DELETE_ENTRY, id: id};
};
const seedProjects = function (list) {
    return {type: SEED_PROJECTS, list: list};
};
const setCurrentProject = function (id) {
    return {type: SET_CURRENT_PROJECT, id: id};
};

export {
    reducer as default,
    initialState as myProjectsInitialState,
    createProjectEntry,
    updateProjectEntry,
    deleteProjectEntry,
    seedProjects,
    setCurrentProject
};
